'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, AlertTriangle, Shield, Users, Clock, Heart, CheckCircle } from 'lucide-react'

export default function SafetyRulesPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-red-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/safety"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          안전 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-red-900/30' : 'bg-red-50'}`}>
            <div className="flex items-center gap-3 xxs:gap-2 mb-3 xxs:mb-2">
              <div className={`p-2 xxs:p-1.5 rounded-lg ${isDark ? 'bg-red-500' : 'bg-red-500'}`}>
                <Shield className="w-6 h-6 xxs:w-5 xxs:h-5 text-white" />
              </div>
              <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                프리다이빙 안전 수칙
              </h1>
            </div>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 절대 규칙 */}
            <div className={`p-4 xxs:p-3 rounded-xl border-l-4 border-red-500 ${
              isDark ? 'bg-red-900/20' : 'bg-red-50'
            }`}>
              <div className="flex items-start gap-3 xxs:gap-2">
                <AlertTriangle className={`w-6 h-6 xxs:w-5 xxs:h-5 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                <div>
                  <h3 className={`font-bold text-lg xxs:text-base mb-1 ${isDark ? 'text-red-300' : 'text-red-900'}`}>
                    절대 규칙
                  </h3>
                  <p className={`text-sm xxs:text-xs ${isDark ? 'text-red-200' : 'text-red-800'}`}>
                    <strong>절대 혼자 다이빙하지 마세요!</strong> 항상 버디와 함께 다이빙해야 합니다.
                  </p>
                </div>
              </div>
            </div>

            {/* 필수 안전 수칙 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                필수 안전 수칙
              </h2>
              <div className="space-y-3 xxs:space-y-2">
                {[
                  { icon: Users, text: '버디 시스템: 항상 버디와 함께, 서로를 관찰' },
                  { icon: AlertTriangle, text: '과호흡 금지: 과도한 호흡은 블랙아웃 위험 증가' },
                  { icon: CheckCircle, text: '능력 범위 내: 자신의 레벨에 맞는 수심 유지' },
                  { icon: Heart, text: '컨디션 체크: 피곤하거나 아플 때는 다이빙 금지' },
                  { icon: Shield, text: '이퀄라이징: 귀 압력 조절 실패 시 즉시 상승' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 xxs:gap-2 p-3 xxs:p-2 rounded-lg ${
                      isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                    }`}
                  >
                    <div className={`p-1.5 xxs:p-1 rounded-full ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                      <item.icon className={`w-4 h-4 xxs:w-3.5 xxs:h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <span className={`text-sm xxs:text-xs ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 풀 다이빙 안전 수칙 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                풀 다이빙 안전 수칙
              </h2>
              <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <ul className={`space-y-2 xxs:space-y-1.5 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li className="flex items-start gap-2">
                    <Clock className="w-4 h-4 xxs:w-3.5 xxs:h-3.5 flex-shrink-0 mt-0.5" />
                    <span>수면 휴식 시간을 충분히 확보 (다이빙 시간의 2배 이상)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 xxs:w-3.5 xxs:h-3.5 flex-shrink-0 mt-0.5" />
                    <span>웜업과 쿨다운을 반드시 실시</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 xxs:w-3.5 xxs:h-3.5 flex-shrink-0 mt-0.5" />
                    <span>다이빙 전 최소 2시간 전에 식사 완료</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 xxs:w-3.5 xxs:h-3.5 flex-shrink-0 mt-0.5 text-red-400" />
                    <span>음주 후 24시간 이내 다이빙 금지</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 버디 찾기 CTA */}
            <div className={`p-5 xxs:p-4 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-blue-300' : 'text-gray-900'}`}>
                안전한 버디 찾기
              </h3>
              <p className={`text-sm xxs:text-xs mb-4 xxs:mb-3 ${isDark ? 'text-blue-200/80' : 'text-gray-600'}`}>
                오늘다이빙에서 안전하고 신뢰할 수 있는 프리다이빙 버디를 찾아보세요.
              </p>
              <Link
                href="/matching/new"
                className="inline-block px-5 xxs:px-4 py-2.5 xxs:py-2 text-sm xxs:text-xs font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                버디 매칭 신청
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
